import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  hrmsGetRequest,
  hrmsPostRequest,
  hrmsUpdateRequest,
} from "../services";
import toast from "react-hot-toast";

const initialState = {
  siteVisits: [],
  activeSiteVisit: null,
  loading: false,
  error: null,
};

export const initiateAdminSiteVisit = createAsyncThunk(
  "siteVisit/initiate",
  async (
    { adminEmail, adminName, initiatedByEmail, ...initData },
    { rejectWithValue }
  ) => {
    try {
      const response = await hrmsPostRequest("/api/admin-site-visits/initiate", {
        adminEmail,
        adminName,
        initiatedByEmail,
        ...initData,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message || error.message || "Failed to initiate site visit"
      );
    }
  }
);

export const fetchSiteVisitsByEmail = createAsyncThunk(
  "siteVisit/fetchByEmail",
  async (adminEmail, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(
        `/api/admin-site-visits/email/${encodeURIComponent(adminEmail)}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchActiveSiteVisit = createAsyncThunk(
  "siteVisit/fetchActive",
  async (adminEmail, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(
        `/api/admin-site-visits/active/${encodeURIComponent(adminEmail)}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const uploadSiteVisitAttachment = createAsyncThunk(
  "siteVisit/uploadAttachment",
  async (files, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));

      const response = await hrmsPostRequest(
        "/api/admin-site-visits/upload-attachment",
        formData,
        true
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message || error.message || "Failed to upload attachment"
      );
    }
  }
);

export const submitObservationsSiteVisit = createAsyncThunk(
  "siteVisit/observationsSubmit",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(
        `/api/admin-site-visits/${id}/observations`,
        formData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message ||
          error.message ||
          "Failed to submit observation evidence"
      );
    }
  }
);

export const saveAdminPreSiteVisit = createAsyncThunk(
  "siteVisit/adminPreSave",
  async ({ id, adminEmail, formData }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(
        `/api/admin-site-visits/${id}/admin-pre`,
        { ...formData, adminEmail }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message ||
          error.message ||
          "Failed to save pre-observation"
      );
    }
  }
);

export const saveAdminDraftSiteVisit = createAsyncThunk(
  "siteVisit/adminDraft",
  async ({ id, adminEmail, formData }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(
        `/api/admin-site-visits/${id}/admin-draft`,
        { ...formData, adminEmail }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message ||
          error.message ||
          "Failed to save site visit draft"
      );
    }
  }
);

export const submitAdminSiteVisit = createAsyncThunk(
  "siteVisit/adminSubmit",
  async ({ id, adminEmail, formData }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(
        `/api/admin-site-visits/${id}/admin-submit`,
        { ...formData, adminEmail }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message || error.message || "Failed to submit site visit"
      );
    }
  }
);

export const submitFeedbackSiteVisit = createAsyncThunk(
  "siteVisit/feedbackSubmit",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(
        `/api/admin-site-visits/${id}/feedback`,
        formData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message ||
          error.message ||
          "Failed to submit evaluator feedback"
      );
    }
  }
);

export const reinitiateAdminSiteVisit = createAsyncThunk(
  "siteVisit/reinitiate",
  async (id, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(
        `/api/admin-site-visits/${id}/reinitiate`,
        {}
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message || error.message || "Failed to reinitiate site visit"
      );
    }
  }
);

const upsertSiteVisit = (state, payload) => {
  const existingIndex = state.siteVisits.findIndex(
    (item) => item.id === payload.id
  );
  if (existingIndex !== -1) {
    state.siteVisits[existingIndex] = payload;
  } else {
    state.siteVisits = [payload, ...state.siteVisits];
  }
};

const siteVisitSlice = createSlice({
  name: "siteVisit",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(initiateAdminSiteVisit.pending, (state) => {
        state.loading = true;
      })
      .addCase(initiateAdminSiteVisit.fulfilled, (state, action) => {
        state.loading = false;
        upsertSiteVisit(state, action.payload);
        toast.success("Site visit initiated successfully");
      })
      .addCase(initiateAdminSiteVisit.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload);
      })
      .addCase(fetchSiteVisitsByEmail.fulfilled, (state, action) => {
        state.siteVisits = action.payload;
      })
      .addCase(fetchActiveSiteVisit.fulfilled, (state, action) => {
        state.activeSiteVisit = action.payload;
      })
      .addCase(submitObservationsSiteVisit.pending, (state) => {
        state.loading = true;
      })
      .addCase(submitObservationsSiteVisit.fulfilled, (state, action) => {
        state.loading = false;
        upsertSiteVisit(state, action.payload);
        toast.success("Observation evidence saved. Leader can now complete Post-Observation.");
      })
      .addCase(submitObservationsSiteVisit.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload);
      })
      .addCase(saveAdminPreSiteVisit.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveAdminPreSiteVisit.fulfilled, (state, action) => {
        state.loading = false;
        upsertSiteVisit(state, action.payload);
        state.activeSiteVisit = action.payload;
        toast.success("Pre-Observation saved");
      })
      .addCase(saveAdminPreSiteVisit.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload);
      })
      .addCase(saveAdminDraftSiteVisit.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveAdminDraftSiteVisit.fulfilled, (state, action) => {
        state.loading = false;
        upsertSiteVisit(state, action.payload);
        state.activeSiteVisit = action.payload;
        toast.success("Progress saved. You can continue later.");
      })
      .addCase(saveAdminDraftSiteVisit.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload);
      })
      .addCase(submitAdminSiteVisit.pending, (state) => {
        state.loading = true;
      })
      .addCase(submitAdminSiteVisit.fulfilled, (state, action) => {
        state.loading = false;
        upsertSiteVisit(state, action.payload);
        state.activeSiteVisit = null;
        toast.success("Site visit submitted successfully");
      })
      .addCase(submitAdminSiteVisit.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload);
      })
      .addCase(submitFeedbackSiteVisit.pending, (state) => {
        state.loading = true;
      })
      .addCase(submitFeedbackSiteVisit.fulfilled, (state, action) => {
        state.loading = false;
        upsertSiteVisit(state, action.payload);
        toast.success("Site visit completed and locked");
      })
      .addCase(submitFeedbackSiteVisit.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload);
      })
      .addCase(reinitiateAdminSiteVisit.fulfilled, (state, action) => {
        upsertSiteVisit(state, action.payload);
        state.activeSiteVisit = action.payload;
        toast.success("Site visit reinitiated successfully");
      })
      .addCase(reinitiateAdminSiteVisit.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(uploadSiteVisitAttachment.rejected, (_, action) => {
        toast.error(action.payload);
      });
  },
});

export default siteVisitSlice.reducer;
